import { UserId } from '../user/User';
import { UploadId } from '../upload';
import { AssetIdAndLayerId, BuilderTemplateId } from '../builderTemplate';
import { PathBuilder, ApiRoute, HttpMethod, Url, isNotNullOrUndefined } from '../common';
import { LocationId } from '../location';
import { WorkflowStartResult } from '../external';
import { PublishedArtifactGroupId, PublishedArtifact, PublishedArtifactId } from '../publishedArtifact';
import { IProcessVideoOutput } from '../video/video';
import { IAuditDataWithChannel } from './audit';
import { IPublishVideoRequest } from './video';
import { IPublishImageRequest } from './image';

export const MAX_INSTAGRAM_CAPTION_CHARACTER_LENGTH = 2200;
export const MAX_INSTAGRAM_HASHTAGS_COUNT = 30;
export const MAX_INSTAGRAM_CAROUSEL_MEDIA_COUNT = 10;

// We only allow stories with a 9:16 (0.5625:1) aspect ratio
export const VALID_INSTAGRAM_STORY_RATIO = 0.5625;

export interface IInstagramPublishData {
    caption?: string | null;
    firstComment?: string | null;
    isStory?: boolean;
    scheduledTime: Date | null;
    notificationPhoneNumber: string | null;
    assignedUserId: UserId;
    publishedArtifactId?: PublishedArtifactId | null;
}

export type InstagramDirectPublishFormData = Pick<IInstagramPublishData, 'caption' | 'firstComment' | 'scheduledTime'>;
export type InstagramStoryDirectPublishFormData = Pick<IInstagramPublishData, 'scheduledTime'>;

export interface InstagramPublishBody extends IInstagramPublishData {
    builderTemplateId: BuilderTemplateId;
    plannerId?: number | null;
}

export interface InstagramPublishMediaBody extends InstagramPublishBody {
    uploadId: UploadId;
    linkedAssetLibraryAssetIds?: AssetIdAndLayerId[];
    publishedArtifactGroupId?: PublishedArtifactGroupId | null;
}

export type IPublishVideoToInstagramRequest = IPublishVideoRequest<IInstagramPublishData>;

export type PublishPhotoToInstagramRequest = IPublishImageRequest<IInstagramPublishData>;

export type AnyInstagramPublishWorkflowInput =
    | IPublishVideoToInstagramWorkflowInput
    | PublishImageToInstagramWorkflowInput
    | ReschedulePublishVideoToInstagramWorkflowInput;

export interface IPublishVideoToInstagramWorkflowInput {
    channelData: IInstagramPublishData;
    /**
     * Tagged as any for now because the actual typing is in app-web and shouldn't be in shared
     * The actual typing is `VideoProcessorPayload`
     * in `apps/app-web/server/domain/video/types/video-processor-payload.ts`
     */
    videoData: any;
    auditData: IAuditDataWithChannel;
    scheduledTime?: Date;
}

export function isIPublishVideoToInstagramWorkflowInput(
    workflowInput: AnyInstagramPublishWorkflowInput,
): workflowInput is IPublishVideoToInstagramWorkflowInput {
    return isNotNullOrUndefined((workflowInput as IPublishVideoToInstagramWorkflowInput).videoData);
}

export interface ReschedulePublishVideoToInstagramWorkflowInput {
    channelData: IInstagramPublishData;
    outputVideoData: IProcessVideoOutput;
    auditData: IAuditDataWithChannel;
    scheduledTime?: Date;
}

export function isReschedulePublishVideoToInstagramWorkflowInput(
    workflowInput: AnyInstagramPublishWorkflowInput,
): workflowInput is ReschedulePublishVideoToInstagramWorkflowInput {
    return isNotNullOrUndefined((workflowInput as ReschedulePublishVideoToInstagramWorkflowInput).outputVideoData);
}

export interface PublishImageToInstagramWorkflowInput {
    channelData: IInstagramPublishData;
    uploadIds: UploadId[];
    auditData: IAuditDataWithChannel;
    scheduledTime: Date;
}

export interface InstagramPublishResult {
    pageId: string;
    postId: string;
}

export const INSTAGRAM_LINK_EXPIRY_WEEKS = 1;

interface InstagramPublishBaseParams {
    locationId: LocationId;
}
const INSTAGRAM_PUBLISH_API_ROOT = <T extends InstagramPublishBaseParams>(path: PathBuilder<T>) =>
    path.literal('publish').literal('instagram').literal('location').param('locationId');

export const InstagramPublishApi = {
    publishPhoto: new ApiRoute<PublishedArtifact, InstagramPublishBaseParams, InstagramPublishMediaBody, void>(
        HttpMethod.Post,
        path => INSTAGRAM_PUBLISH_API_ROOT(path).literal('photo'),
    ),
    publishPhotoDirect: new ApiRoute<
        WorkflowStartResult,
        InstagramPublishBaseParams,
        PublishPhotoToInstagramRequest,
        void
    >(HttpMethod.Post, path => INSTAGRAM_PUBLISH_API_ROOT(path).literal('photoDirect')),
    publishVideo: new ApiRoute<WorkflowStartResult, InstagramPublishBaseParams, IPublishVideoToInstagramRequest, void>(
        HttpMethod.Post,
        path => INSTAGRAM_PUBLISH_API_ROOT(path).literal('video'),
    ),
    publishVideoDirect: new ApiRoute<
        WorkflowStartResult,
        InstagramPublishBaseParams,
        IPublishVideoToInstagramRequest,
        void
    >(HttpMethod.Post, path => INSTAGRAM_PUBLISH_API_ROOT(path).literal('videoDirect')),
};

export function instagramPermalink(postId: string): Url {
    return Url.from(`https://www.instagram.com/p/${postId}`);
}

/**
 * Instagram API rate limit for a sliding 24 hour window.
 *
 * @see https://developers.facebook.com/docs/instagram-api/guides/content-publishing
 */
export const INSTAGRAM_API_RATE_LIMIT = 25;

/**
 * Represents the number rate limit for publishing on a certain date.
 */
export interface PublishRateLimit {
    limitReached: boolean;
    current: number;
    remaining: number;
}

/**
 * Check for whether Instagram caption is below the required instagram limit.
 *
 * @param caption - string to check the character limit
 * @returns - boolean whether the caption is below the max character limit
 */
export function isBelowInstagramDirectMaxCharacterLimit(caption: string): boolean {
    return caption.length <= MAX_INSTAGRAM_CAPTION_CHARACTER_LENGTH;
}
